import {
  DataIngestionInProgressIndicator,
  DisabledIntegrationIndicator,
} from '@modules/integrationsV2/components/IntegrationStateIndicator';
import { MetadataFieldMappingActionsMenu } from '@modules/integrationsV2/components/LinkIntegrationMetadataSection/MetadataFieldMappingList/MetadataFieldMappingActionsMenu';
import { MetadataFieldMappingAutoComplete } from '@modules/integrationsV2/components/LinkIntegrationMetadataSection/MetadataFieldMappingList/MetadataFieldMappingAutoComplete';
import { MetadataFieldMappingLinkIcon } from '@modules/integrationsV2/components/LinkIntegrationMetadataSection/MetadataFieldMappingList/MetadataFieldMappingLinkIcon';
import { MetadataFieldMappingStateIndicator } from '@modules/integrationsV2/components/LinkIntegrationMetadataSection/MetadataFieldMappingList/MetadataFieldMappingStateIndicator';
import { useUpdateMetadataFieldRelations } from '@modules/integrationsV2/hooks';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import { IntegrationSource } from '@modules/integrationsV2/types';
import {
  getExistingFieldsRelation,
  trackIntegrationPageAddLinkEvent,
} from '@modules/integrationsV2/utils';
import { Box, Stack, Typography } from '@mui/material';
import { EqlField } from 'generated/graphql';
import { useEffect, useState } from 'react';

interface MetadataFieldMappingListItemProps {
  sourceIntegrationSource: IntegrationSource;
  targetIntegrationSource: IntegrationSource;
  eqlFields: EqlField[];
}

export const MetadataFieldMappingListItem = ({
  sourceIntegrationSource,
  targetIntegrationSource,
  eqlFields,
}: MetadataFieldMappingListItemProps) => {
  const fieldRelationsByType = useIntegrationsStore(
    ({ data }) => data.fieldRelationsByType,
  );
  const isFieldRelationsQueryInProgress = useIntegrationsStore(
    ({ view }) => view.isFieldRelationsQueryInProgress,
  );

  const existingFieldsRelation = getExistingFieldsRelation({
    fieldRelationsByType,
    sourceIntegrationSource,
    targetIntegrationSource,
  });

  const relatedSourceField =
    existingFieldsRelation?.[sourceIntegrationSource.integrationType]?.field ??
    '';

  const relatedTargetField =
    existingFieldsRelation?.[targetIntegrationSource.integrationType]?.field ??
    '';

  const [sourceField, setSourceField] = useState(relatedSourceField);
  const [targetField, setTargetField] = useState(relatedTargetField);

  useEffect(() => {
    setSourceField(relatedSourceField);
  }, [relatedSourceField]);

  useEffect(() => {
    setTargetField(relatedTargetField);
  }, [relatedTargetField]);

  const [updateFieldRelations, { isLoading: isUpdatingFieldRelations }] =
    useUpdateMetadataFieldRelations();

  const sourceEqlField = eqlFields.find(
    (eqlField) => eqlField.field === sourceField,
  );

  const targetEqlField = eqlFields.find(
    (eqlField) => eqlField.field === targetField,
  );

  const sourceIntegrationFieldDetails = sourceEqlField
    ? {
        displayName: sourceEqlField.displayName,
        objectType: sourceEqlField.objectType,
      }
    : undefined;

  const targetIntegrationFieldDetails = targetEqlField
    ? {
        displayName: targetEqlField.displayName,
        objectType: targetEqlField.objectType,
      }
    : undefined;

  const onTargetFieldChange = async (
    _: React.SyntheticEvent<Element, Event>,
    eqlField: EqlField,
  ) => {
    setTargetField(eqlField.field);

    if (!sourceField || !eqlField.field) {
      return;
    }

    try {
      await updateFieldRelations({
        sourceFieldInput: {
          integrationSource: sourceIntegrationSource,
          field: sourceField,
        },
        targetFieldInput: {
          integrationSource: targetIntegrationSource,
          field: eqlField.field,
        },
      });
      if (targetEqlField && sourceEqlField) {
        trackIntegrationPageAddLinkEvent({
          eventName: 'Link Integrations - Metadata Link Added',
          targetIntegrationField: targetIntegrationFieldDetails,
          sourceIntegrationField: sourceIntegrationFieldDetails,
        });
      }
    } catch {
      // do nothing
    }
  };

  const onSourceFieldChange = async (
    _: React.SyntheticEvent<Element, Event>,
    eqlField: EqlField,
  ) => {
    setSourceField(eqlField.field);

    if (!targetField || !eqlField.field) {
      return;
    }

    try {
      await updateFieldRelations({
        sourceFieldInput: {
          integrationSource: sourceIntegrationSource,
          field: eqlField.field,
        },
        targetFieldInput: {
          integrationSource: targetIntegrationSource,
          field: targetField,
        },
      });
      trackIntegrationPageAddLinkEvent({
        eventName: 'Link Integrations - Metadata Link Added',
        targetIntegrationField: targetIntegrationFieldDetails,
        sourceIntegrationField: sourceIntegrationFieldDetails,
      });
    } catch {
      // do nothing
    }
  };

  const onRemoveLink = async () => {
    if (!isLinked) {
      return;
    }
    try {
      await updateFieldRelations({
        sourceFieldInput: {
          integrationSource: sourceIntegrationSource,
          field: sourceField,
        },
        targetFieldInput: {
          integrationSource: targetIntegrationSource,
          field: targetField,
        },
        isRemoveAction: true,
      });
      trackIntegrationPageAddLinkEvent({
        eventName: 'Link Integrations - Metadata Link Removed',
        targetIntegrationField: targetIntegrationFieldDetails,
        sourceIntegrationField: sourceIntegrationFieldDetails,
      });
    } catch {
      // do nothing
    }
  };

  const isIngestionInProgress = targetIntegrationSource.isIngestionInProgress;

  const isIntegrationDisabled = targetIntegrationSource.isDisabled;

  const isLinked = Boolean(relatedSourceField && relatedTargetField);

  const isLinkingDisabled =
    isIngestionInProgress ||
    isUpdatingFieldRelations ||
    isFieldRelationsQueryInProgress;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1.5}
    >
      <Stack minWidth="100px" spacing={0.5}>
        <Stack
          direction="row"
          alignItems="center"
          columnGap={1}
          rowGap={0.5}
          flexWrap="wrap"
        >
          <Typography>{targetIntegrationSource.channel.displayName}</Typography>
          {isIntegrationDisabled && (
            <DisabledIntegrationIndicator variant="icon" />
          )}
          {isIngestionInProgress && (
            <DataIngestionInProgressIndicator variant="icon" />
          )}
          <MetadataFieldMappingStateIndicator isLinked={isLinked} />
        </Stack>
        {targetIntegrationSource.namespace && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {targetIntegrationSource.namespace}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <MetadataFieldMappingAutoComplete
          integrationSource={targetIntegrationSource}
          onChange={onTargetFieldChange}
          value={targetEqlField}
          isDisabled={isLinkingDisabled}
          isLoading={isFieldRelationsQueryInProgress}
          isLinked={isLinked}
        />
        <Stack width="80px" alignItems="center" justifyContent="center">
          <MetadataFieldMappingLinkIcon
            sourceEqlField={sourceEqlField}
            targetEqlField={targetEqlField}
            isLoading={
              isUpdatingFieldRelations || isFieldRelationsQueryInProgress
            }
            isLinked={isLinked}
          />
        </Stack>
        <MetadataFieldMappingAutoComplete
          integrationSource={sourceIntegrationSource}
          onChange={onSourceFieldChange}
          value={sourceEqlField}
          isDisabled={isLinkingDisabled}
          isLoading={isFieldRelationsQueryInProgress}
          isLinked={isLinked}
        />
        <Box width="24px">
          {isLinked && !isLinkingDisabled && (
            <MetadataFieldMappingActionsMenu onRemoveLink={onRemoveLink} />
          )}
        </Box>
      </Stack>
    </Stack>
  );
};
